import type { ClientLegacyStaysResponse } from '@guilleumes/types'
import api from './api'

export interface Client {
  id: string
  nom: string
  cognoms: string
  nom_complet: string
  email: string
  telefon?: string | null
  mobil?: string | null
  tipus_document?: string | null
  num_document?: string | null
  adreca?: string | null
  ciutat?: string | null
  provincia?: string | null
  codi_postal?: string | null
  pais?: string | null
  empresa?: string | null
  nif_empresa?: string | null
  total_reserves: number
  total_gastat: number
  ultima_reserva?: string | null
  primera_reserva?: string | null
  actiu: boolean
  es_vip: boolean
  tipus_client?: string | null
  idioma_preferit?: string | null
  /** Persona de contacte vinculada; `null` en empreses sense persona (sense notes fins a F6) */
  persona_id?: string | null
  /** Notes internes: viuen a `Person.notes` */
  notes?: string | null
  consentiment_marketing: boolean
  /** Client o empresa de confiança: paga en arribar (`plans/calendar-card-signals.md` §3.6). */
  payOnArrival?: boolean
  data_consentiment?: string | null
  origen?: string | null
  created_at: string
  updated_at: string
}

export interface ClientsResponse {
  data: Client[]
  meta: {
    current_page: number
    per_page: number
    total: number
    total_pages: number
  }
}

/**
 * Resultat de `create` i `update`: el backend torna el `Client` pelat (sense `success` ni
 * `data`); el servei l'embolcalla aquí perquè `useEntityMutation` hi mira `success`. Els
 * errors HTTP es tornen com a `{ success: false, message }`. `getById` torna el `Client` tal qual.
 */
export interface ClientResponse {
  success: boolean
  data?: Client
  message?: string
  errors?: Record<string, string[]>
}

class ClientsApiService {
  async getAll(params: any = {}): Promise<ClientsResponse> {
    try {
      const response = await api.get('/clients', { params })
      return response.data
    } catch (error: any) {
      console.error('Error fetching clients:', error)
      throw error
    }
  }

  async getById(id: string | number): Promise<Client> {
    try {
      const response = await api.get<Client>(`/clients/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching client:', error)
      throw error
    }
  }

  async create(clientData: Partial<Client>): Promise<ClientResponse> {
    try {
      const response = await api.post<Client>('/clients', clientData)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Error creating client:', error)
      
      // Si és un error de validació (422), retornar l'error en el format esperat
      if (error.response && error.response.status === 422) {
        return {
          success: false,
          errors: error.response.data.errors || {},
          message: error.response.data.message || 'Error de validació'
        }
      }
      
      // Per altres errors, retornar un error genèric
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en crear el client'
        }
      }
      
      // Error de connexió o altres
      return {
        success: false,
        message: 'Error de connexió'
      }
    }
  }

  async update(id: string | number, clientData: Partial<Client>): Promise<ClientResponse> {
    try {
      const response = await api.put<Client>(`/clients/${id}`, clientData)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Error updating client:', error)
      
      // Si és un error de validació (422), retornar l'error en el format esperat
      if (error.response && error.response.status === 422) {
        return {
          success: false,
          errors: error.response.data.errors || {},
          message: error.response.data.message || 'Error de validació'
        }
      }
      
      // Per altres errors, retornar un error genèric
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en actualitzar el client'
        }
      }
      
      // Error de connexió o altres
      return {
        success: false,
        message: 'Error de connexió'
      }
    }
  }

  async delete(id: string | number): Promise<ClientResponse> {
    try {
      const response = await api.delete(`/clients/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error deleting client:', error)
      throw error
    }
  }

  /**
   * Estades del PMS antic (2010-2021) del client: resum i llista, de més recent a més
   * antiga. `plans/legacy-pms-import.md` §L4. Va a part de `getById` perquè la llista no
   * té sostre i la fitxa només la demana quan la pinta.
   */
  async getLegacyStays(id: string): Promise<ClientLegacyStaysResponse> {
    const response = await api.get<ClientLegacyStaysResponse>(`/clients/${id}/legacy-stays`)
    return response.data
  }

  async getHistorialReserves(id: string) {
    try {
      const response = await api.get(`/clients/${id}/historial-reserves`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching client reservation history:', error)
      throw error
    }
  }
}

export const ClientsService = new ClientsApiService()
